import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import handbookService from './HandbookService';
import {
	Alert,
	Badge,
	Button,
	ButtonGroup,
	Card,
	CardBody,
	CardHeader,
	Col,
	FormGroup,
	Input,
	Label,
	Row,
	Spinner,
	Table
} from 'reactstrap';

export function HandbookAdminList() {
	const [sections, setSections] = useState([]);
	const [selectedSectionId, setSelectedSectionId] = useState('');
	const [items, setItems] = useState([]);
	const [loadingSections, setLoadingSections] = useState(true);
	const [loadingItems, setLoadingItems] = useState(false);
	const [error, setError] = useState('');

	useEffect(() => {
		loadSections();
	}, []);

	const selectedSection = useMemo(
		() => sections.find(x => x.id === selectedSectionId) || null,
		[sections, selectedSectionId]
	);

	const loadSections = async () => {
		setLoadingSections(true);
		setError('');
		try {
			const data = await handbookService.getAdminSections();
			const sectionList = data || [];
			setSections(sectionList);
			if (sectionList.length > 0) {
				const nextSectionId = sectionList.some(x => x.id === selectedSectionId) ? selectedSectionId : sectionList[0].id;
				setSelectedSectionId(nextSectionId);
				await loadItems(nextSectionId);
			} else {
				setSelectedSectionId('');
				setItems([]);
			}
		} catch (e) {
			setError('Could not load handbook sections. Please try again.');
		} finally {
			setLoadingSections(false);
		}
	};

	const loadItems = async (sectionId) => {
		if (!sectionId) {
			setItems([]);
			return;
		}
		setLoadingItems(true);
		setError('');
		try {
			const data = await handbookService.getAdminItems(sectionId);
			setItems(data || []);
		} catch (e) {
			setError('Could not load section items. Please try again.');
		} finally {
			setLoadingItems(false);
		}
	};

	const deleteSection = async (id) => {
		if (!window.confirm('Delete this section and all of its items? This cannot be undone.')) return;
		await handbookService.deleteSection(id);
		await loadSections();
	};

	const deleteItem = async (id) => {
		if (!window.confirm('Delete this item?')) return;
		await handbookService.deleteItem(id);
		await loadItems(selectedSectionId);
	};

	const onSectionSelect = async (event) => {
		const sectionId = event.target.value;
		setSelectedSectionId(sectionId);
		await loadItems(sectionId);
	};

	return (
		<div>
			<Row className="mb-3">
				<Col md={8}>
					<h1 className="mb-1">Volunteer Handbook Admin</h1>
					<p className="text-muted mb-0">Manage handbook sections, resources, links, PDFs, people cards, and photos.</p>
				</Col>
				<Col md={4} className="text-md-right mt-3 mt-md-0">
					<Link to="/handbook/admin/sections/add" className="btn btn-primary">Add Section</Link>
				</Col>
			</Row>

			{error && <Alert color="danger">{error}</Alert>}

			<Card className="mb-4">
				<CardHeader className="d-flex justify-content-between align-items-center">
					<strong>Sections</strong>
					<Button color="secondary" size="sm" onClick={loadSections}>Refresh</Button>
				</CardHeader>
				<CardBody>
					{loadingSections ? (
						<div><Spinner size="sm" /> Loading sections...</div>
					) : sections.length === 0 ? (
						<Alert color="info" className="mb-0">No handbook sections yet. Click <strong>Add Section</strong> to create your first section.</Alert>
					) : (
						<Table striped responsive>
							<thead>
								<tr>
									<th>Name</th>
									<th>Key</th>
									<th>Order</th>
									<th>Status</th>
									<th style={{ width: '220px' }}>Actions</th>
								</tr>
							</thead>
							<tbody>
								{sections.map(section => (
									<tr key={section.id} className={selectedSectionId === section.id ? 'table-primary' : ''}>
										<td>{section.name}</td>
										<td><code>{section.key}</code></td>
										<td>{section.displayOrder}</td>
										<td>{section.enabled ? <Badge color="success">Enabled</Badge> : <Badge color="secondary">Disabled</Badge>}</td>
										<td>
											<ButtonGroup size="sm">
												<Button color="info" onClick={async () => { setSelectedSectionId(section.id); await loadItems(section.id); }}>View Items</Button>
												<Link className="btn btn-warning" to={`/handbook/admin/sections/edit/${section.id}`}>Edit</Link>
												<Button color="danger" onClick={() => deleteSection(section.id)}>Delete</Button>
											</ButtonGroup>
										</td>
									</tr>
								))}
							</tbody>
						</Table>
					)}
				</CardBody>
			</Card>

			<Card>
				<CardHeader className="d-flex justify-content-between align-items-center">
					<strong>Items {selectedSection ? `for "${selectedSection.name}"` : ''}</strong>
					{selectedSectionId && <Link to={`/handbook/admin/items/add/${selectedSectionId}`} className="btn btn-primary btn-sm">Add Item</Link>}
				</CardHeader>
				<CardBody>
					<FormGroup>
						<Label for="sectionSelect">Choose section</Label>
						<Input id="sectionSelect" type="select" value={selectedSectionId || ''} onChange={onSectionSelect} disabled={sections.length === 0}>
							<option value="">-- Select a section --</option>
							{sections.map(section => (
								<option key={section.id} value={section.id}>{section.name}</option>
							))}
						</Input>
					</FormGroup>

					{loadingItems ? (
						<div><Spinner size="sm" /> Loading items...</div>
					) : !selectedSectionId ? (
						<Alert color="info" className="mb-0">Select a section to view and manage its items.</Alert>
					) : items.length === 0 ? (
						<Alert color="light" className="mb-0">No items yet in this section. Click <strong>Add Item</strong> to create the first item.</Alert>
					) : (
						<Table striped responsive>
							<thead>
								<tr>
									<th>Title</th>
									<th>Subsection</th>
									<th>Type</th>
									<th>Order</th>
									<th>Status</th>
									<th style={{ width: '180px' }}>Actions</th>
								</tr>
							</thead>
							<tbody>
								{items.map(item => (
									<tr key={item.id}>
										<td>{item.title}</td>
										<td><Badge color="dark">{item.subsectionType || 'Guidance'}</Badge></td>
										<td><Badge color="primary">{item.itemType}</Badge></td>
										<td>{item.displayOrder}</td>
										<td>{item.enabled ? <Badge color="success">Enabled</Badge> : <Badge color="secondary">Disabled</Badge>}</td>
										<td>
											<ButtonGroup size="sm">
												<Link className="btn btn-warning" to={`/handbook/admin/items/edit/${item.id}`}>Edit</Link>
												<Button color="danger" onClick={() => deleteItem(item.id)}>Delete</Button>
											</ButtonGroup>
										</td>
									</tr>
								))}
							</tbody>
						</Table>
					)}
				</CardBody>
			</Card>
		</div>
	);
}
